import { useAuth0 } from "@auth0/auth0-react";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { GetAllUsersAPI, GetCurrentUserApi } from "../api/userApiCalls";
import {
  resetAuthentication,
  setAccessToken,
  setPermission,
  setUser,
} from "../store/features/authentication/authenticationSlice";
import {
  setUsers,
} from "../store/features/users/usersSlice";

import { IUser } from "../utilities/interfaces";
import { useAppDispatch } from "../store/hooks";
import { ShowLoading } from "../utilities/functions";
import { setTokenSession } from "./tokenInterceptor";
import { endSession, endSessionIfExpired } from "./session";
import ErrorPermissionPage from "../pages/ErrorPermissionPage/ErrorPermissionPage";

interface AuthenticationMiddlewareProps {
  children: React.ReactNode;
}

// "denied": the backend refused this account or doesn't know it (401/403/404 or no user)
type UserStatus = "loading" | "ready" | "denied" | "error";

/** A failed CurrentUser request that retrying may fix (no response, a timeout, or a server error). */
function isRecoverableError(error: any): boolean {
  const status = error?.response?.status;
  return !status || status >= 500;
}

const AuthenticationMiddleware: React.FC<AuthenticationMiddlewareProps> = ({
  children,
}) => {
  const { isLoading, isAuthenticated, user, getAccessTokenSilently, logout } = useAuth0();
  const dispatch = useAppDispatch();
  const [userStatus, setUserStatus] = useState<UserStatus>("loading");
  const [attempt, setAttempt] = useState(0);

  // Hand the Auth0 session to the axios interceptors, which attach a fresh token to every
  // backend request. A layout effect runs before any child's useEffect, so the session is in
  // place for the pages' first requests without changing global state during render.
  useLayoutEffect(() => {
    if (!isAuthenticated) {
      setTokenSession(null);
      return;
    }
    setTokenSession({
      getToken: getAccessTokenSilently,
      // Keep the Redux copy current for pages that read the token with GetUserToken()
      onToken: (token) => dispatch(setAccessToken(token)),
      onSessionExpired: () => endSession(logout),
    });
    return () => setTokenSession(null);
  }, [isAuthenticated, getAccessTokenSilently, logout, dispatch]);

  useEffect(() => {
    // Wait for Auth0 to finish loading, otherwise isAuthenticated is still false
    if (isLoading) return;

    if (!isAuthenticated) {
      dispatch(resetAuthentication());
      return;
    }

    let cancelled = false;
    setUserStatus("loading");

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        if (cancelled) return;
        dispatch(setAccessToken(token));

        // The users list only feeds pickers: load it on the side so it never holds up sign-in
        GetAllUsersAPI(token).then(([allUsers, allUsersError]) => {
          if (cancelled) return;
          if (allUsersError) {
            console.error("Error loading users:", allUsersError);
          } else if (allUsers) {
            dispatch(setUsers(allUsers as IUser[]));
          }
        });

        // The API helpers return [data, error] instead of throwing
        const [currentUser, currentUserError] = await GetCurrentUserApi(token);
        if (cancelled) return;

        if (currentUserError) {
          console.error("Error loading the signed-in user:", currentUserError);
          setUserStatus(isRecoverableError(currentUserError) ? "error" : "denied");
          return;
        }
        if (!currentUser) {
          setUserStatus("denied");
          return;
        }

        dispatch(setPermission((currentUser as IUser).userPermissionId));
        dispatch(setUser(currentUser));
        setUserStatus("ready");
      } catch (error) {
        if (cancelled) return;
        console.error("Error getting the access token:", error);
        if (endSessionIfExpired(error, logout)) return;
        setUserStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoading, isAuthenticated, getAccessTokenSilently, logout, dispatch, attempt]);

  // Hold the pages back until the backend user and permissions are loaded, so they don't
  // render with a null user. Unverified accounts skip this: they only see the verify-email page.
  const waitForUser = !isLoading && isAuthenticated && !!user?.email_verified;

  if (waitForUser && userStatus === "loading") {
    return ShowLoading();
  }

  // Accounts the backend refuses never reach the protected pages
  if (waitForUser && userStatus === "denied") {
    return <ErrorPermissionPage />;
  }

  if (waitForUser && userStatus === "error") {
    return (
      <div className="d-flex justify-content-center mt-5">
        <div className="alert text-center bg-alert" role="alert">
          <h4>
            <i className="fas fa-exclamation-triangle fa-1x"></i> Your account
            couldn't be loaded
          </h4>
          <p className="mt-2">
            The server is not reachable. Check your network connection and try again.
          </p>
          <div className="mt-4">
            <button
              type="button"
              className="primary-btn"
              onClick={() => setAttempt((count) => count + 1)}
            >
              Try again
            </button>
            <button
              type="button"
              className="btn btn-link"
              onClick={() => endSession(logout)}
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthenticationMiddleware;
